import React, { useEffect } from 'react'

import { Check, X } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PasswordInput } from '@/shared/components/atoms/PasswordInput'

interface RegisterStep1Props {
	form: UseFormReturn<RegisterRequestDTO>
	isEmailAvailable: boolean | null
	isCheckingEmailAvailability?: boolean
	isLoading: boolean
	onNextStep: (formData: Partial<RegisterRequestDTO>) => void
}

export const RegisterStep1: React.FC<RegisterStep1Props> = ({
	form,
	isEmailAvailable,
	isCheckingEmailAvailability,
	isLoading,
	onNextStep,
}) => {
	const { t } = useTranslation('auth')

	const handleNextStep = async () => {
		const isValid = await form.trigger([
			'firstname',
			'lastname',
			'email',
			'password',
			'password_confirmation',
		])

		const currentPassword = form.getValues('password') || ''
		const passwordRequirements = validatePasswordRequirements(currentPassword)

		const allPasswordRequirementsMet =
			passwordRequirements.minLength &&
			passwordRequirements.hasNumber &&
			passwordRequirements.hasSpecialChar

		if (isValid && allPasswordRequirementsMet) {
			const { firstname, lastname, email, password, password_confirmation } =
				form.getValues()

			const stepFormData = {
				firstname: firstname as string,
				lastname: lastname as string,
				email: email as string,
				password: password,
				password_confirmation: password_confirmation,
			}

			onNextStep(stepFormData)
		}
	}

	const validatePasswordRequirements = (password: string) => {
		return {
			minLength: password.length >= 8,
			hasNumber: /\d/.test(password),
			hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
		}
	}

	const currentPassword = form.watch('password') || ''
	const passwordRequirements = validatePasswordRequirements(currentPassword)

	const allPasswordRequirementsMet =
		passwordRequirements.minLength &&
		passwordRequirements.hasNumber &&
		passwordRequirements.hasSpecialChar

	const email = form.watch('email') || ''
	const firstname = form.watch('firstname') || ''
	const lastname = form.watch('lastname') || ''
	const password = form.watch('password') || ''
	const passwordConfirmation = form.watch('password_confirmation') || ''

	// Trigger validation of password_confirmation when password changes
	useEffect(() => {
		if (passwordConfirmation) {
			form.trigger('password_confirmation')
		}
	}, [password, passwordConfirmation, form])

	const isFormValid =
		firstname.length > 0 &&
		lastname.length > 0 &&
		email.length > 0 &&
		password.length > 0 &&
		passwordConfirmation.length > 0 &&
		allPasswordRequirementsMet &&
		isEmailAvailable === true &&
		Object.keys(form.formState.errors).length === 0

	return (
		<div className="space-y-4">
			<h1 className="text-4xl font-bold mb-0">{t('register.title')}</h1>

			<p className="mb-4">{t('register.createAccountInstruction')}</p>

			<div className="space-y-4">
				<div>
					<Label className="text-xs mb-2 block">
						{t('register.firstname')}
					</Label>
					<Controller
						control={form.control}
						name="firstname"
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									placeholder={t('register.firstname')}
									className="h-10"
									{...field}
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="text-xs mb-2 block">{t('register.lastname')}</Label>
					<Controller
						control={form.control}
						name="lastname"
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									placeholder={t('register.lastname')}
									className="h-10"
									{...field}
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="text-xs mb-2 block">{t('register.email')}</Label>
					<Controller
						control={form.control}
						name="email"
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									placeholder={t('register.email').toLowerCase()}
									className="h-10"
									{...field}
								/>
								{isEmailAvailable === false && (
									<ErrorMessage
										error={t('register.errors.emailNotAvailable')}
									/>
								)}
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="text-xs mb-2 block">{t('register.password')}</Label>
					<Controller
						control={form.control}
						name="password"
						render={({ field, fieldState: { error } }) => (
							<div>
								<PasswordInput
									placeholder={t('register.password')}
									className="h-10"
									{...field}
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="text-xs mb-2 block">
						{t('register.confirmPassword')}
					</Label>
					<Controller
						control={form.control}
						name="password_confirmation"
						render={({ field, fieldState: { error } }) => (
							<div>
								<PasswordInput
									placeholder={t('register.confirmPassword')}
									className="h-10"
									{...field}
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div className="space-y-2 mt-2">
					<div className="flex items-center gap-3">
						{passwordRequirements.minLength ? (
							<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
						) : (
							<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
						)}
						<span
							className={`text-xs leading-4 ${passwordRequirements.minLength ? 'text-emerald-500' : 'text-gray-400'}`}
						>
							{t('register.passwordRequirements.minLength')}
						</span>
					</div>
					<div className="flex items-center gap-3">
						{passwordRequirements.hasNumber ? (
							<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
						) : (
							<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
						)}
						<span
							className={`text-xs leading-4 ${passwordRequirements.hasNumber ? 'text-emerald-500' : 'text-gray-400'}`}
						>
							{t('register.passwordRequirements.hasNumber')}
						</span>
					</div>
					<div className="flex items-center gap-3">
						{passwordRequirements.hasSpecialChar ? (
							<Check className="h-4 w-4 text-green-400 flex-shrink-0" />
						) : (
							<X className="h-4 w-4 text-gray-400 flex-shrink-0" />
						)}
						<span
							className={`text-xs leading-4 ${passwordRequirements.hasSpecialChar ? 'text-emerald-500' : 'text-gray-400'}`}
						>
							{t('register.passwordRequirements.hasSpecialChar')}
						</span>
					</div>
				</div>
			</div>

			<div className="mt-8 flex justify-center">
				<Button
					onClick={handleNextStep}
					className="h-10 px-8 py-3 cursor-pointer w-full text-sm font-medium rounded-full"
					disabled={isLoading || isCheckingEmailAvailability || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('register.continue')}
				</Button>
			</div>
		</div>
	)
}
